import apiClient from './client';

export interface AdAccount {
  _id: string;
  // 'manual' — lead source with no ad API (Hebe, Rabota): leads/deals from
  // Zoho, spend entered by hand as ManualSpendEntry rows.
  platform: 'facebook' | 'tiktok' | 'google' | 'youtube' | 'manual';
  name: string;
  platformAccountId: string;
  isActive: boolean;
  /**
   * Optional name-filter substring. When set, only ads with this in `ad_name`
   * and Zoho deals with this in `field3` are included in this account's view.
   * `null` / empty = no filter.
   */
  adNameFilter?: string | null;
  lastSyncAt?: string;
  syncStatus: 'idle' | 'syncing' | 'error';
  syncError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAccountRequest {
  platform: 'facebook' | 'tiktok' | 'google' | 'youtube';
  name: string;
  platformAccountId: string;
  accessToken: string;
  refreshToken?: string;
}

export async function getAccounts(): Promise<AdAccount[]> {
  const response = await apiClient.get('/accounts');
  return response.data.accounts;
}

export async function getAccount(id: string): Promise<AdAccount> {
  const response = await apiClient.get(`/accounts/${id}`);
  return response.data.account;
}

export async function createAccount(data: CreateAccountRequest): Promise<AdAccount> {
  const response = await apiClient.post('/accounts', data);
  return response.data.account;
}

export async function updateAccount(
  id: string,
  data: Partial<CreateAccountRequest & { isActive: boolean; adNameFilter: string | null }>
): Promise<AdAccount> {
  const response = await apiClient.put(`/accounts/${id}`, data);
  return response.data.account;
}

export async function deleteAccount(id: string): Promise<void> {
  await apiClient.delete(`/accounts/${id}`);
}

export async function testAccountConnection(
  id: string
): Promise<{ success: boolean; data?: unknown; error?: string }> {
  const response = await apiClient.post(`/accounts/${id}/test`);
  return response.data;
}

// ---- Manual spend (manual-platform accounts: Hebe, Rabota) ----
// The model is a DAILY RATE: one number that accrues every day from
// `effectiveFrom` until replaced by a newer rate (effectiveTo = null means
// currently active).

export interface ManualSpendEntry {
  _id: string;
  accountId: string;
  month: string;   // YYYY-MM
  amount: number;  // ILS total for that month
  createdAt: string;
}

export async function getManualSpend(accountId: string): Promise<ManualSpendEntry[]> {
  const response = await apiClient.get(`/accounts/${accountId}/manual-spend`);
  return response.data.entries;
}

export async function setManualSpendMonth(
  accountId: string,
  month: string,
  amount: number
): Promise<ManualSpendEntry> {
  const response = await apiClient.post(`/accounts/${accountId}/manual-spend`, { month, amount });
  return response.data.entry;
}

export async function deleteManualSpend(accountId: string, entryId: string): Promise<void> {
  await apiClient.delete(`/accounts/${accountId}/manual-spend/${entryId}`);
}
